import { DEFAULT_TIMEZONE, type DeliverableStatus } from "@dream/config/constants";
import {
  type CalendarFilters,
  localDateKey,
  localMonthRange,
  localWeekRange,
  parseClientHandleFromPath,
  parseFocusDate,
  startOfLocalDay,
} from "@dream/domain";
import { Button, Card, Skeleton } from "@dream/ui";
import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../api/client.ts";
import type { CalendarCommand } from "../calendar/adapter/mapping.ts";
import { DreamCalendar } from "../calendar/dream-calendar.tsx";
import { CalendarEmptyHint } from "../components/calendar-empty-hint.tsx";
import { CalendarToolbar } from "../components/calendar-toolbar.tsx";
import { ClientMark } from "../components/client-mark.tsx";
import { PinInput } from "../components/pin-input.tsx";
import { PortalFrame, PortalMessage } from "../components/portal-chrome.tsx";
import { PortalDeliverableDialog } from "../components/portal-deliverable-dialog.tsx";
import { occurrencesToUpcomingItems, UpcomingRail } from "../components/upcoming-rail.tsx";
import {
  useClientViewCalendar,
  useClientViewContext,
  useClientViewPreview,
  useClientViewUnlock,
  useInvalidateCalendar,
} from "../hooks/use-calendar.ts";
import { useRealtime } from "../hooks/use-realtime.ts";
import { useMediaQuery, useTimelineDays } from "../hooks/use-viewport.ts";
import { formatRelativeWhen } from "../lib/schedule-copy.ts";
import { validateCalendarSearch } from "../router.tsx";

/**
 * Visão do cliente: calendar.dreamvision.com.br/@javalibarber
 *
 * Sem token na URL. A senha de 4 dígitos é a que a operação definiu no
 * cadastro. Depois do PIN, o calendário é somente daquele cliente.
 */

const PORTAL_SKELETON_KEYS = Array.from({ length: 35 }, (_, i) => `client-view-celula-${i}`);

function parseCsv(value: string | undefined): string[] {
  return value === undefined ? [] : value.split(",").filter((v) => v !== "");
}

function toCsv(values: string[]): string | undefined {
  return values.length === 0 ? undefined : values.join(",");
}

export function ClientViewPage() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const handle = parseClientHandleFromPath(pathname) ?? "";
  const search = validateCalendarSearch(
    (useSearch({ strict: false, shouldThrow: false }) as Record<string, unknown> | undefined) ?? {},
  );
  const view = search.view ?? "mes";
  const groupBy = search.groupBy ?? "none";
  const colorBy = search.colorBy ?? "type";
  const navigate = useNavigate();
  const invalidateCalendar = useInvalidateCalendar();
  const isPhone = useMediaQuery("(max-width: 639px)");
  const appliedPhoneAgenda = useRef(false);

  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState<string | null>(null);
  const [detail, setDetail] = useState<string | null>(search.entrega ?? null);
  const timelineDays = useTimelineDays();

  const preview = useClientViewPreview(handle);
  const unlock = useClientViewUnlock(handle);
  const context = useClientViewContext(handle, preview.data?.hasPin === true);

  const timezone =
    (context.data as { timezone?: string } | undefined)?.timezone ?? DEFAULT_TIMEZONE;

  const focusDate = useMemo(() => parseFocusDate(search.date, timezone), [search.date, timezone]);

  const range = useMemo(() => {
    if (view === "dia") {
      const start = startOfLocalDay(focusDate, timezone);
      return {
        start: DateTime.fromJSDate(start, { zone: timezone }).minus({ days: 1 }).toJSDate(),
        end: DateTime.fromJSDate(start, { zone: timezone }).plus({ days: 2 }).toJSDate(),
      };
    }
    if (view === "semana") {
      const week = localWeekRange(focusDate, timezone);
      return {
        start: DateTime.fromJSDate(week.start, { zone: timezone }).minus({ days: 1 }).toJSDate(),
        end: DateTime.fromJSDate(week.end, { zone: timezone }).plus({ days: 1 }).toJSDate(),
      };
    }
    if (view === "linha") {
      const start = startOfLocalDay(focusDate, timezone);
      return {
        start,
        end: DateTime.fromJSDate(start, { zone: timezone })
          .plus({ days: timelineDays + 1 })
          .toJSDate(),
      };
    }
    const month = localMonthRange(focusDate, timezone);
    return {
      start: DateTime.fromJSDate(month.start, { zone: timezone }).minus({ days: 7 }).toJSDate(),
      end: DateTime.fromJSDate(month.end, { zone: timezone }).plus({ days: 7 }).toJSDate(),
    };
  }, [focusDate, view, timezone, timelineDays]);

  const filters = useMemo<CalendarFilters>(
    () => ({
      clientIds: [],
      projectIds: parseCsv(search.projects),
      typeIds: parseCsv(search.types),
      assigneeIds: [],
      statuses: parseCsv(search.statuses) as DeliverableStatus[],
      tagIds: [],
      search: search.q ?? null,
    }),
    [search],
  );

  const queryParams = useMemo(
    () => ({
      start: range.start.toISOString(),
      end: range.end.toISOString(),
      filters,
    }),
    [range, filters],
  );

  const contextReady = context.data !== undefined;
  const upcomingParams = useMemo(() => {
    const start = DateTime.now().setZone(timezone).startOf("day");
    return {
      start: start.toUTC().toISO() ?? range.start.toISOString(),
      end: start.plus({ days: 14 }).toUTC().toISO() ?? range.end.toISOString(),
      filters: {
        clientIds: [] as string[],
        projectIds: [] as string[],
        typeIds: [] as string[],
        assigneeIds: [] as string[],
        statuses: [] as DeliverableStatus[],
        tagIds: [] as string[],
        search: null,
      },
    };
  }, [timezone, range.start, range.end]);
  const calendar = useClientViewCalendar(handle, queryParams, contextReady);
  const upcomingCalendar = useClientViewCalendar(handle, upcomingParams, contextReady);

  const connection = useRealtime({
    handle,
    enabled: contextReady,
    onChange: (message) => invalidateCalendar(message.at),
    onResync: invalidateCalendar,
  });

  const setSearch = useCallback(
    (patch: Partial<typeof search>) => {
      if (handle === "") return;
      void navigate({
        to: "/@{$handle}",
        params: { handle },
        search: (prev) => ({
          ...validateCalendarSearch(prev as Record<string, unknown>),
          ...patch,
        }),
        replace: true,
      });
    },
    [handle, navigate],
  );

  useEffect(() => {
    if (!isPhone || appliedPhoneAgenda.current) return;
    appliedPhoneAgenda.current = true;
    if (view === "mes") {
      setSearch({ view: "agenda" });
    }
  }, [isPhone, view, setSearch]);

  const handleNavigate = useCallback(
    (direction: "prev" | "next" | "today") => {
      if (direction === "today") {
        setSearch({ date: undefined });
        return;
      }
      const step = direction === "next" ? 1 : -1;
      const current = DateTime.fromJSDate(focusDate, { zone: timezone });
      const next =
        view === "dia"
          ? current.plus({ days: step })
          : view === "semana"
            ? current.plus({ weeks: step })
            : view === "linha"
              ? current.plus({ days: timelineDays * step })
              : current.plus({ months: step });
      setSearch({ date: localDateKey(next.toJSDate(), timezone) });
    },
    [focusDate, view, setSearch, timezone, timelineDays],
  );

  const handleCommand = useCallback(
    (command: CalendarCommand) => {
      if (command.kind === "open") {
        setDetail(command.deliverableId);
        setSearch({ entrega: command.deliverableId });
      }
    },
    [setSearch],
  );

  async function submitPin(code = pin): Promise<void> {
    if (code.length !== 4) {
      setPinError("A senha tem 4 dígitos.");
      return;
    }
    setPinError(null);
    try {
      await unlock.mutateAsync(code);
      setPin("");
    } catch (error) {
      setPinError(error instanceof ApiError ? error.message : "Não foi possível validar a senha.");
    }
  }

  const brand = preview.data;

  if (preview.isLoading) {
    return (
      <PortalFrame>
        <div className="space-y-3 p-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="mt-6 h-96" />
        </div>
      </PortalFrame>
    );
  }

  if (preview.error !== null || brand === undefined) {
    return (
      <PortalFrame>
        <PortalMessage
          title="Calendário não encontrado"
          message="Confira o endereço. O link certo tem o formato calendar.dreamvision.com.br/@cliente."
        />
      </PortalFrame>
    );
  }

  if (!brand.hasPin) {
    return (
      <PortalFrame clientColor={brand.color} clientLogoUrl={brand.logoUrl} gateName={brand.name}>
        <PortalMessage
          title={`${brand.name} ainda não tem senha`}
          message="A equipe da Dream Vision precisa definir a senha de 4 dígitos no cadastro deste cliente. Depois disso, este endereço passa a abrir o calendário."
        />
      </PortalFrame>
    );
  }

  const needsPin =
    context.error instanceof ApiError &&
    (context.error.status === 401 || context.error.isForbidden);

  if (context.error !== null && !needsPin) {
    return (
      <PortalFrame clientColor={brand.color} clientLogoUrl={brand.logoUrl} gateName={brand.name}>
        <PortalMessage
          title="Não foi possível abrir o calendário"
          message={
            context.error instanceof ApiError
              ? context.error.message
              : "Tente de novo em instantes."
          }
        />
      </PortalFrame>
    );
  }

  if (
    (context.isLoading && context.data === undefined && !needsPin) ||
    (unlock.isSuccess && context.data === undefined)
  ) {
    return (
      <PortalFrame clientColor={brand.color} clientLogoUrl={brand.logoUrl} gateName={brand.name}>
        <div className="space-y-3 p-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-6 h-96" />
        </div>
      </PortalFrame>
    );
  }

  if (needsPin || context.data === undefined) {
    return (
      <PortalFrame clientColor={brand.color} clientLogoUrl={brand.logoUrl} gateName={brand.name}>
        <div className="flex flex-1 items-center justify-center px-4 py-10 sm:py-16">
          <Card className="dream-enter w-full max-w-md overflow-hidden p-0">
            <div className="px-5 py-6 sm:px-8 sm:py-8">
              <div className="flex justify-center">
                <ClientMark
                  alt={brand.name}
                  color={brand.color}
                  logoUrl={brand.logoUrl}
                  name={brand.name}
                  size={isPhone ? 80 : 96}
                />
              </div>
              <p className="mt-4 text-center text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[var(--color-ink-muted)]">
                Sua agenda de entregas com a Dream Vision
              </p>
              <h1
                className="mt-2 text-center text-[1.5rem] font-bold leading-tight tracking-[0] text-[var(--color-ink)]"
                data-testid="client-view-name"
              >
                {brand.name}
              </h1>
              <p className="mx-auto mt-2 max-w-sm text-center text-[0.875rem] leading-5 text-[var(--color-ink-secondary)]">
                Quatro dígitos que a equipe passou. Depois disso, você vê a agenda de entregas de{" "}
                {brand.name} — sem login, sem edição.
              </p>

              <form
                className="mt-6 space-y-4 sm:mt-7 sm:space-y-5"
                onSubmit={(event) => {
                  event.preventDefault();
                  void submitPin();
                }}
              >
                <PinInput
                  id="client-view-pin"
                  value={pin}
                  invalid={pinError !== null}
                  autoFocus
                  disabled={unlock.isPending}
                  onChange={(next) => {
                    setPin(next);
                    setPinError(null);
                  }}
                  onComplete={(code) => {
                    void submitPin(code);
                  }}
                />
                {pinError !== null && (
                  <p
                    role="alert"
                    className="text-center text-[0.8125rem] font-medium text-[#AB3434]"
                  >
                    {pinError}
                  </p>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  loading={unlock.isPending}
                  disabled={pin.length !== 4}
                >
                  Ver calendário
                </Button>
              </form>
            </div>
          </Card>
        </div>
      </PortalFrame>
    );
  }

  const data = context.data as {
    client: {
      id: string;
      name: string;
      color: string;
      handle?: string;
      logoUrl?: string | null;
    } | null;
    projects: Array<{
      id: string;
      name: string;
      color: string;
      status: string;
      clientId: string;
    }>;
    types: Array<{
      id: string;
      label: string;
      color: string;
      icon: string | null;
      slug: string;
    }>;
  };

  const occurrences = calendar.data?.occurrences ?? [];
  const upcomingItems = occurrencesToUpcomingItems(
    upcomingCalendar.data?.occurrences ?? [],
    data.types,
    data.client === null ? [] : [data.client],
  );
  const lateCount = upcomingItems.filter((item) => item.status === "late").length;
  const next = upcomingItems[0];
  const nextWhen =
    next === undefined ? null : formatRelativeWhen(next.startsAt, timezone, next.isAllDay);
  const nextLine =
    next === undefined || nextWhen === null ? null : `${nextWhen.day} · ${next.title}`;
  const summary =
    upcomingItems.length === 0
      ? "Nenhuma entrega nas próximas duas semanas."
      : lateCount > 0
        ? `Próxima: ${nextLine}. ${upcomingItems.length} no período · ${lateCount} atrasada${lateCount === 1 ? "" : "s"}`
        : `Próxima: ${nextLine}`;
  const hasActiveFilters =
    filters.search !== null ||
    filters.statuses.length > 0 ||
    filters.projectIds.length > 0 ||
    filters.typeIds.length > 0;
  const calendarReady = calendar.data !== undefined || calendar.error !== null;

  return (
    <PortalFrame
      clientName={data.client?.name ?? brand.name}
      clientColor={data.client?.color ?? brand.color}
      clientLogoUrl={data.client?.logoUrl ?? brand.logoUrl}
      eyebrow="Sua agenda de entregas"
      blurb="Tudo o que a Dream Vision está produzindo para você. Toque numa entrega para ver tipo, data e status. Este calendário é só para acompanhar — sem edição."
      summary={summary}
    >
      <CalendarToolbar
        view={view}
        focusDate={focusDate}
        timezone={timezone}
        groupBy={groupBy}
        colorBy={colorBy}
        search={search.q ?? ""}
        statuses={filters.statuses}
        clientIds={[]}
        clients={data.client === null ? [] : [data.client]}
        connection={connection.state}
        loading={calendar.isFetching}
        resultCount={occurrences.length}
        readOnly
        periodAsHeading={false}
        projectIds={filters.projectIds}
        typeIds={filters.typeIds}
        projects={data.projects}
        types={data.types}
        onViewChange={(next) => setSearch({ view: next === "mes" ? undefined : next })}
        onNavigate={handleNavigate}
        onGroupByChange={(next) => setSearch({ groupBy: next === "none" ? undefined : next })}
        onColorByChange={(next) => setSearch({ colorBy: next === "type" ? undefined : next })}
        onSearchChange={(q) => setSearch({ q: q === "" ? undefined : q })}
        onStatusesChange={(statuses) => setSearch({ statuses: toCsv(statuses) })}
        onClientIdsChange={() => {
          // A visão do cliente já é escopada no servidor.
        }}
        onProjectIdsChange={(projectIds) => setSearch({ projects: toCsv(projectIds) })}
        onTypeIdsChange={(typeIds) => setSearch({ types: toCsv(typeIds) })}
        onClearFilters={() =>
          setSearch({
            q: undefined,
            statuses: undefined,
            types: undefined,
            projects: undefined,
            groupBy: undefined,
          })
        }
      />

      {calendarReady ? (
        <UpcomingRail
          empty="Nada agendado nas próximas duas semanas. Assim que a equipe lançar, aparece aqui."
          heading="Próximas entregas"
          hideWhenEmpty
          items={upcomingItems}
          onOpen={(deliverableId) => {
            setDetail(deliverableId);
            setSearch({ entrega: deliverableId });
          }}
          timezone={timezone}
        />
      ) : null}

      {hasActiveFilters && occurrences.length === 0 && calendarReady && calendar.error === null && (
        <CalendarEmptyHint readOnly filtered />
      )}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-[var(--color-surface)]">
        {calendar.isLoading && calendar.data === undefined ? (
          <div className="p-4">
            <div className="grid grid-cols-7 gap-2">
              {PORTAL_SKELETON_KEYS.map((key) => (
                <Skeleton key={key} className="h-24" />
              ))}
            </div>
          </div>
        ) : (
          <DreamCalendar
            view={view}
            occurrences={occurrences}
            types={data.types}
            clients={data.client === null ? [] : [data.client]}
            timezone={timezone}
            focusDate={focusDate}
            colorBy="type"
            groupBy="none"
            interactive={false}
            loading={calendar.isFetching}
            onCommand={handleCommand}
            projects={data.projects}
          />
        )}
      </div>

      {detail !== null && (
        <PortalDeliverableDialog
          handle={handle}
          deliverableId={detail}
          timezone={timezone}
          clientName={data.client?.name ?? brand.name}
          clientColor={data.client?.color ?? brand.color}
          projects={data.projects}
          types={data.types}
          onClose={() => {
            setDetail(null);
            setSearch({ entrega: undefined });
          }}
        />
      )}
    </PortalFrame>
  );
}
